import type { Interface } from '@unifyapps/defs/types/interface';
import { getEvaluatedThemeId } from './themeConditionalUtils';

interface EvaluateThemeIdParams {
  defaultThemeId: NonNullable<Interface['theme']>['defaultThemeId'];
  context: Record<string, unknown>;
  preferredThemeId?: string;
  shouldEvaluateResult?: (result: string) => boolean;
}

/**
 * Evaluates the theme ID to use based on priority:
 * 1. Preferred theme (if provided) (can be set from cookie for anonymous users or user preference for logged in users)
 * 2. Conditional themes (evaluated from defaultThemeId)
 * 3. Interface defaultThemeId
 * 4. Platform defaultThemeId (handled by getEvaluatedThemeId)
 *
 * @param defaultThemeId - The interface's defaultThemeId (can be string or conditional value filter)
 * @param context - Compute context for evaluating conditional themes
 * @param preferredThemeId - Optional preferred theme ID (highest priority)
 * @returns The evaluated theme ID string, or undefined if no theme is available
 */
export function evaluateThemeId({
  defaultThemeId,
  context,
  preferredThemeId,
  shouldEvaluateResult,
}: EvaluateThemeIdParams): string | undefined {
  // Preferred theme has highest priority
  if (preferredThemeId) {
    return preferredThemeId;
  }

  // Evaluate conditional themes and default theme
  // Priority: Conditional themes → Interface defaultThemeId → Platform defaultThemeId
  return getEvaluatedThemeId(defaultThemeId, context, shouldEvaluateResult);
}
